import React, { useState, useRef, useEffect } from 'react'
import {
  Wrapper,
  ScrollArea,
  Table,
  HeaderRow,
  BodyRow,
  Th,
  StickyTh,
  Td,
  StickyTd,
  SortIndicator,
  MenuCell,
  CompactTh,
  EmptyCell,
  TableControls,
  ToggleWrapper,
  ColumnsButton,
  ColumnsDropdown,
  ShowAllButton,
  ColumnToggleItem,
  RowMenuPanel,
  RowMenuOption,
} from './HonestTable.styled'
import { SquareCheckBox } from 'components/CheckBox/CheckBox.styled'
import { CheckBoxIcon } from 'components/CheckBox/CheckBoxIcon'
import { ThreeDots } from 'assets/icons/ThreeDots'
import { FloatingDropdown } from 'components/FloatingDropdown/FloatingDropdown'
import { EditContainer } from 'styling/GlobalStyles'
import { BodyText, OtherText } from 'styling/Typography'
import { JustSpinner } from 'components/Loading/Loading'
import { handleOptionBtn } from 'utils/helpers'

export interface IHonestTableColumn<T> {
  id: string
  header: string
  cell: (item: T) => React.ReactNode
  sortable?: boolean
  defaultVisible?: boolean
  minWidth?: string
  align?: 'left' | 'center' | 'right'
  sticky?: boolean
  fill?: boolean
  compact?: boolean
}

export interface IHonestTableMenuOption<T> {
  label: string
  icon?: React.ReactNode
  onClick: (item: T) => void
  color?: string
}

interface IHonestTableProps<T> {
  columns: IHonestTableColumn<T>[]
  data: T[]
  keyField: keyof T
  onClick?: (item: T) => void
  sortField?: string
  sortDirection?: 'asc' | 'desc'
  onSort?: (field: string) => void
  loading?: boolean
  columnToggle?: boolean
  emptyMessage?: string
  menuOptions?: IHonestTableMenuOption<T>[] | ((item: T) => IHonestTableMenuOption<T>[])
}

const RowMenu = <T,>({
  item,
  options,
}: {
  item: T & { id: string | number }
  options: IHonestTableMenuOption<T>[]
}) => {
  const [editID, setEditID] = useState<any>(false)
  const [showEditMenu, setShowEditMenu] = useState(false)
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const isOpen = editID === item.id && showEditMenu

  const closeMenu = () => {
    setShowEditMenu(false)
    setAnchor(null)
  }

  return (
    <EditContainer
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation()
        setAnchor(e.currentTarget)
        handleOptionBtn(item, setEditID, setShowEditMenu, Number(editID))
      }}
      show={isOpen}
    >
      <span>
        <ThreeDots />
      </span>
      <FloatingDropdown
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) closeMenu()
        }}
        reference={isOpen ? anchor : null}
        placement='left-start'
      >
        <RowMenuPanel>
          {options.map((opt, i) => (
            <RowMenuOption
              key={i}
              onClick={(e) => {
                e.stopPropagation()
                opt.onClick(item)
                closeMenu()
              }}
            >
              {opt.icon}
              <OtherText color={opt.color}>{opt.label}</OtherText>
            </RowMenuOption>
          ))}
        </RowMenuPanel>
      </FloatingDropdown>
    </EditContainer>
  )
}

export const HonestTable = <T extends { id: string | number },>({
  columns,
  data,
  keyField,
  onClick,
  sortField,
  sortDirection = 'desc',
  onSort,
  loading,
  columnToggle,
  emptyMessage = 'No data available',
  menuOptions,
}: IHonestTableProps<T>) => {
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {}
    columns.forEach((col) => {
      initial[col.id] = col.defaultVisible !== false
    })
    return initial
  })
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const toggleColumn = (id: string) => {
    setVisibleColumns((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const showAll = () => {
    const all: Record<string, boolean> = {}
    columns.forEach((col) => {
      all[col.id] = true
    })
    setVisibleColumns(all)
  }

  const getMenuOptions = (item: T): IHonestTableMenuOption<T>[] => {
    if (!menuOptions) return []
    return typeof menuOptions === 'function' ? menuOptions(item) : menuOptions
  }

  const isCompactColumn = (col: IHonestTableColumn<T>) => col.compact || col.id === 'actions'

  const getColumnStyle = (col: IHonestTableColumn<T>) => ({
    minWidth: col.minWidth,
    ...(col.fill ? { width: '100%' } : {}),
  })

  const activeColumns = columns.filter((col) => col.fill || visibleColumns[col.id])
  const totalColumns = activeColumns.length + (menuOptions ? 1 : 0)

  return (
    <div>
      {columnToggle && (
        <TableControls>
          <ToggleWrapper ref={dropdownRef}>
            <ColumnsButton onClick={() => setShowDropdown((prev) => !prev)}>
              Columns
            </ColumnsButton>
            {showDropdown && (
              <ColumnsDropdown>
                <ShowAllButton onClick={showAll}>Show all</ShowAllButton>
                {columns.filter((col) => !col.fill).map((col) => (
                  <ColumnToggleItem key={col.id} onClick={() => toggleColumn(col.id)}>
                    <SquareCheckBox active={visibleColumns[col.id]}>
                      {visibleColumns[col.id] && <CheckBoxIcon />}
                    </SquareCheckBox>
                    <BodyText>{col.header}</BodyText>
                  </ColumnToggleItem>
                ))}
              </ColumnsDropdown>
            )}
          </ToggleWrapper>
        </TableControls>
      )}
      <Wrapper>
        <ScrollArea>
        <Table>
          <thead>
            <HeaderRow>
              {activeColumns.map((col) => {
                if (isCompactColumn(col)) {
                  return <CompactTh key={col.id} $align={col.align} />
                }

                const Cell = col.sticky ? StickyTh : Th
                return (
                  <Cell
                    key={col.id}
                    $align={col.align}
                    $sortable={col.sortable}
                    onClick={() => col.sortable && onSort?.(col.id)}
                    style={getColumnStyle(col)}
                  >
                    {col.header}
                    {sortField === col.id && (
                      <SortIndicator>
                        {sortDirection === 'desc' ? '▼' : '▲'}
                      </SortIndicator>
                    )}
                  </Cell>
                )
              })}
              {menuOptions && <Th style={{ width: '3rem', padding: 0 }} />}
            </HeaderRow>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={totalColumns}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '25rem',
                    }}
                  >
                    <JustSpinner />
                  </div>
                </td>
              </tr>
            ) : data.length > 0 ? (
              data.map((item) => {
                const rowKey = String(item[keyField])
                return (
                  <BodyRow key={rowKey} $clickable={!!onClick} onClick={() => onClick?.(item)}>
                    {activeColumns.map((col) => {
                      if (isCompactColumn(col)) {
                        return (
                          <MenuCell
                            key={col.id}
                            onClick={(e) => {
                              e.stopPropagation()
                            }}
                          >
                            {col.cell(item)}
                          </MenuCell>
                        )
                      }

                      const Cell = col.sticky ? StickyTd : Td
                      return (
                        <Cell
                          key={col.id}
                          $align={col.align}
                          style={getColumnStyle(col)}
                        >
                          {col.cell(item)}
                        </Cell>
                      )
                    })}
                    {menuOptions && (
                      <MenuCell
                        onClick={(e) => {
                          e.stopPropagation()
                        }}
                      >
                        <RowMenu item={item} options={getMenuOptions(item)} />
                      </MenuCell>
                    )}
                  </BodyRow>
                )
              })
            ) : (
              <tr>
                <EmptyCell colSpan={totalColumns}>{emptyMessage}</EmptyCell>
              </tr>
            )}
          </tbody>
        </Table>
        </ScrollArea>
      </Wrapper>
    </div>
  )
}

